import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  UserPlus,
  User,
  CreditCard,
  Calendar,
  Layers,
  MapPin,
  ShieldCheck,
  Plus,
  Trash2,
  AlertTriangle,
} from 'lucide-react';

import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Modal } from '@/shared/components/ui/Modal';
import { useMasterItems } from '@/features/master-data/hooks/useMasters';
import { useContact, useContactMutations } from '../hooks/useContacts';
import { ROUTES } from '@/routes/paths';
import { staffIdentityProblem } from '@/shared/utils/staffIdentity';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { api } from '@/shared/api/axios';

/**
 * The save routes (POST/PATCH /api/contacts-identity) reject an invalid
 * GSTIN/PAN with a specific, useful reason — e.g. "GSTIN must be 15
 * characters." (validateContactIdentity, src/shared/contacts/identity.js) —
 * in a `errors: [{ field, message }]` array, alongside a generic top-level
 * `error: 'Validation failed.'`. Showing only that generic string left a
 * rep with no way to tell WHY the save failed or which field to fix.
 */
function contactSaveErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  const fieldError = Array.isArray(data?.errors) ? data.errors[0]?.message : null;
  return fieldError || data?.error || fallback;
}

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

// Contacts imported from the old system spell states many ways ("GUJARAT",
// "Jammu & Kashmir", "MH", "Pondicherry"…). Match them to the dropdown's names
// so an old client's state shows (and saves) instead of reading "Select State".
const STATE_ALIASES: Record<string, string> = {
  mh: 'Maharashtra', gujrat: 'Gujarat', pondicherry: 'Puducherry', orissa: 'Odisha',
  'andaman and nicobar': 'Andaman and Nicobar Islands', 'daman and diu': 'Dadra and Nagar Haveli and Daman and Diu',
  'dadra and nagar haveli': 'Dadra and Nagar Haveli and Daman and Diu', 'new delhi': 'Delhi', 'nct of delhi': 'Delhi',
  uttaranchal: 'Uttarakhand', 'j and k': 'Jammu and Kashmir', 'tamilnadu': 'Tamil Nadu', 'chattisgarh': 'Chhattisgarh',
};
const stateKey = (v: string) => v.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z ]/g, ' ').replace(/\s+/g, ' ').trim();
function normalizeIndianState(raw: string): string {
  const value = String(raw || '').trim();
  if (!value) return '';
  const key = stateKey(value);
  return INDIAN_STATES.find((s) => stateKey(s) === key) || STATE_ALIASES[key] || value;
}

// India first — the overwhelming majority of contacts — then every other
// country a real customer might be in, alphabetically. Choosing a non-India
// country here is what tells the GST engine this is an export: no CGST/SGST/
// IGST applies, and the document offers a manually-entered VAT field instead
// (src/shared/sales/bill-to.js's isInternationalContact()).
const COUNTRIES = [
  'India',
  'Afghanistan', 'Australia', 'Bahrain', 'Bangladesh', 'Belgium', 'Brazil',
  'Canada', 'China', 'Egypt', 'France', 'Germany', 'Hong Kong', 'Indonesia',
  'Iran', 'Iraq', 'Israel', 'Italy', 'Japan', 'Jordan', 'Kenya', 'Kuwait',
  'Malaysia', 'Maldives', 'Mexico', 'Myanmar', 'Nepal', 'Netherlands',
  'New Zealand', 'Nigeria', 'Oman', 'Pakistan', 'Philippines', 'Qatar',
  'Russia', 'Saudi Arabia', 'Singapore', 'South Africa', 'South Korea',
  'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Tanzania', 'Thailand',
  'Turkey', 'UAE', 'Uganda', 'United Kingdom', 'United States', 'Vietnam',
  'Other',
];

const DEFAULT_CATEGORIES = [
  'Dealership', 'Trader', 'Distributor', 'Retailer', 'Direct Client',
  'OEM / Manufacturer', 'Service Provider', 'Wholesaler', 'Other'
];

const CONTACT_TYPES = [
  { value: 'organization', label: 'Organization / Business' },
  { value: 'individual', label: 'Individual' },
];

const CUSTOMER_TYPES = [
  { value: 'customer', label: 'Customer' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'both', label: 'Both (Customer & Vendor)' },
];

const GENDER_OPTIONS = [
  { value: '', label: 'Select Gender...' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: '', label: 'Select Marital Status...' },
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'divorced', label: 'Divorced' },
];

const TABS = [
  { id: 'basic', label: 'Basic Details', icon: User },
  { id: 'additional', label: 'Additional Details', icon: ShieldCheck },
  { id: 'account', label: 'Account Details', icon: CreditCard },
  { id: 'anniversaries', label: 'Anniversaries', icon: Calendar },
] as const;

type TabId = typeof TABS[number]['id'];

export interface ContactFormPageProps {
  /**
   * Render without the page chrome (back button, page title, outer padding),
   * so the SAME form can be shown inside a dialog. It is deliberately this
   * component rather than a second, smaller one: the cut-down modal that used
   * to serve the pipeline form had no GSTIN, address or state fields, so a
   * contact created there was missing exactly what the quotation raised
   * moments later needs.
   */
  embedded?: boolean;
  /** Called instead of navigating after a successful create. */
  onCreated?: (contact: { id: string; name: string; company: string; phone: string; email: string }) => void;
  /** Called instead of navigating when the user backs out. */
  onCancel?: () => void;
  /**
   * Seeds the form when creating. Whatever was typed into the field that led
   * here — a company name in the pipeline form, a search term in the client
   * picker — is almost always the company, so it opens already filled in
   * rather than making the user type it a second time.
   */
  initialValues?: { name?: string; company?: string };
}

export function ContactFormPage({
  embedded = false, onCreated, onCancel, initialValues,
}: ContactFormPageProps = {}) {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id) && !embedded;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Every exit goes through these, so the routed page keeps navigating while an
  // embedded one hands control back to whoever opened it.
  const leave = (to: string) => (onCancel ? onCancel() : navigate(to));

  // Seeds a brand-new, routed "/contacts/new" the same way `initialValues`
  // seeds an embedded one (e.g. from a client with no CRM contact linked yet
  // — see ClientProfilePage's "Add Contact" action) — never applied when
  // editing or embedded, so it can't clobber a real contact's data.
  const qp = (key: string) => (!embedded && !id ? searchParams.get(key) || '' : '');

  const { data: contact, isLoading: contactLoading, isError } = useContact(id);
  const { createContact, updateContact } = useContactMutations();
  const { data: masterCategories = [] } = useMasterItems('category');
  const { data: masterStates = [] } = useMasterItems('state');

  const [activeTab, setActiveTab] = useState<TabId>('basic');

  const [form, setForm] = useState({
    // Top-Level Classification
    contactType: 'organization' as 'individual' | 'organization',
    customerType: 'customer' as 'customer' | 'vendor' | 'both',
    clientType: '' as 'New Client' | 'Old Client' | '',
    category: 'Dealership',

    // Tab 1: Basic Details — seeded from whatever the caller had already typed
    // (a company name in the pipeline form, a search term in the client picker)
    // so it is not typed twice.
    name: initialValues?.name ?? qp('name'),
    company: initialValues?.company ?? qp('company'),
    phone: qp('phone'),
    whatsapp: '',
    extraPhones: [] as string[],
    email: qp('email'),
    address: qp('address'),
    country: 'India',
    state: qp('state'),
    city: qp('city'),
    pin: qp('pin'),

    // Tab 2: Additional Details
    gstin: qp('gstin'),
    ecomGstin: '',
    panNo: '',
    aadharNo: '',
    gender: '',
    position: '',
    notes: '',

    // Tab 3: Account Details
    creditorsOpening: '',
    debtorsOpening: '',
    creditDays: '',
    creditLimit: '',
    paymentTerms: '',

    // Tab 4: Anniversaries
    maritalStatus: '',
    birthdate: '',
  });

  // Set when a create attempt hits a phone number another BDM already owns —
  // contact visibility is siloed per-creator, so the caller's own search
  // could never have found it. See CONTACT_OWNED_BY_OTHER, POST /api/contacts.
  const [duplicateOwner, setDuplicateOwner] = useState<{
    ownerName: string;
    existingContact: { id: string; name: string; company: string; phone: string; email: string };
  } | null>(null);

  const [duplicateInfo, setDuplicateInfo] = useState<{
    on: string;
    type: string;
    match: { _id: string; name: string; company: string; phone: string; gstin: string; ownerName: string };
    message: string;
  } | null>(null);

  const debouncedPhone = useDebounce(form.phone.trim(), 300);
  const debouncedGstin = useDebounce(form.gstin.trim(), 300);

  useEffect(() => {
    if (isEdit) {
      setDuplicateInfo(null);
      return;
    }
    const cleanPhone = debouncedPhone.replace(/\D/g, '');
    const cleanGstin = debouncedGstin.trim().toUpperCase();
    if (cleanPhone.length < 10 && cleanGstin.length < 15) {
      setDuplicateInfo(null);
      return;
    }

    let active = true;
    api.post('/api/contacts-identity/check-duplicate', {
      phone: cleanPhone.length >= 10 ? cleanPhone : undefined,
      gstin: cleanGstin.length >= 15 ? cleanGstin : undefined,
    }).then(({ data }) => {
      if (active) {
        if (data?.duplicate && data?.match) {
          setDuplicateInfo(data);
        } else {
          setDuplicateInfo(null);
        }
      }
    }).catch(() => {
      if (active) setDuplicateInfo(null);
    });

    return () => { active = false; };
  }, [debouncedPhone, debouncedGstin, isEdit]);

  const [hydrated, setHydrated] = useState(false);

  // Populate data in edit mode
  useEffect(() => {
    if (isEdit && contact && !hydrated) {
      setForm({
        contactType: contact.contactType || 'organization',
        customerType: contact.customerType || 'customer',
        clientType: (contact.clientType as any) || '',
        category: contact.category || 'Dealership',

        name: contact.name || '',
        company: contact.company || '',
        phone: contact.phone || '',
        whatsapp: contact.whatsapp || '',
        extraPhones: contact.extraPhones?.length ? contact.extraPhones : [],
        email: contact.email || '',
        address: contact.address || contact.billingAddress?.street || '',
        country: contact.country || contact.billingAddress?.country || 'India',
        state: normalizeIndianState(contact.state || contact.billingAddress?.state || ''),
        city: contact.city || contact.billingAddress?.city || '',
        pin: contact.pin || contact.billingAddress?.zipCode || '',

        gstin: contact.gstin || '',
        ecomGstin: contact.ecomGstin || '',
        panNo: contact.panNo || '',
        aadharNo: contact.aadharNo || '',
        gender: contact.gender || '',
        position: contact.position || '',
        notes: contact.notes || '',

        creditorsOpening: contact.creditorsOpening != null ? String(contact.creditorsOpening) : '',
        debtorsOpening: contact.debtorsOpening != null ? String(contact.debtorsOpening) : '',
        creditDays: contact.creditDays != null ? String(contact.creditDays) : '',
        creditLimit: contact.creditLimit != null ? String(contact.creditLimit) : '',
        paymentTerms: contact.paymentTerms || '',

        maritalStatus: contact.maritalStatus || '',
        birthdate: contact.birthdate ? contact.birthdate.slice(0, 10) : '',
      });
      setHydrated(true);
    }
  }, [isEdit, contact, hydrated]);

  const set = (k: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    let val = e.target.value;
    if (['gstin', 'ecomGstin', 'panNo'].includes(k)) {
      val = val.toUpperCase();
    }
    setForm((prev) => ({ ...prev, [k]: val }));
  };

  const setExtraPhone = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      extraPhones: prev.extraPhones.map((phone, i) => (i === index ? value : phone)),
    }));
  };

  const addExtraPhone = () => {
    setForm((prev) => ({ ...prev, extraPhones: [...prev.extraPhones, ''] }));
  };

  const removeExtraPhone = (index: number) => {
    setForm((prev) => ({
      ...prev,
      extraPhones: prev.extraPhones.filter((_, i) => i !== index),
    }));
  };

  const categoryOptions = [
    ...DEFAULT_CATEGORIES.map((c) => ({ value: c, label: c })),
    ...masterCategories.filter((m) => !DEFAULT_CATEGORIES.includes(m.name)).map((m) => ({ value: m.name, label: m.name })),
  ];

  const countryOptions = COUNTRIES.map((c) => ({ value: c, label: c }));

  const isIndia = !form.country || form.country.trim().toLowerCase() === 'india';

  const stateOptions = [
    { value: '', label: 'Select State...' },
    // A stored value the list does not know is kept visible rather than blanked.
    ...Array.from(new Set([...INDIAN_STATES, ...masterStates.map((s) => s.name), ...(form.state ? [form.state] : [])])).sort().map((s) => ({
      value: s,
      label: s,
    })),
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // STOP, or the enclosing page's form submits too. When this form is shown
    // embedded in a dialog, that dialog is portalled out of the DOM — but React
    // events propagate along the REACT tree, not the DOM tree, so the submit
    // still reaches whatever form the dialog's trigger lives inside. On the
    // pipeline record page that ran its "Create Record" validation while no
    // client had been linked yet, so saving a contact fired a phantom "Choose
    // the client account this enquiry belongs to" error next to the success.
    e.stopPropagation();
    const name = form.name.trim();
    const company = form.company.trim();
    // Either a contact person name or a company name is required — if no
    // person name is given, the company name is used as the record name so
    // the account still has a meaningful display label.
    if (!name && !company) {
      toast.error('Please enter a contact person name or a company / business name.');
      setActiveTab('basic');
      return;
    }
    const resolvedName = name || company;
    const staffProblem = staffIdentityProblem(
      { name, email: form.email },
      isEdit ? { name: contact?.name, email: contact?.email } : {},
    );
    if (staffProblem) {
      toast.error(staffProblem);
      setActiveTab('basic');
      return;
    }
    if (!form.clientType) {
      toast.error('Please select Client Type (New Client or Old Client).');
      return;
    }

    const payload = {
      contactType: form.contactType,
      customerType: form.customerType,
      clientType: form.clientType,
      category: form.category,
      name: resolvedName,
      company: form.company.trim(),
      phone: form.phone.trim(),
      whatsapp: form.whatsapp.trim(),
      extraPhones: Array.from(new Set(form.extraPhones.map((phone) => phone.trim()).filter(Boolean))),
      email: form.email.trim(),
      address: form.address.trim(),
      country: form.country.trim(),
      state: form.state.trim(),
      city: form.city.trim(),
      pin: form.pin.trim(),
      gstin: form.gstin.trim(),
      ecomGstin: form.ecomGstin.trim(),
      panNo: form.panNo.trim(),
      aadharNo: form.aadharNo.trim(),
      gender: form.gender,
      position: form.position.trim(),
      notes: form.notes.trim(),
      creditorsOpening: form.creditorsOpening !== '' ? Number(form.creditorsOpening) : 0,
      debtorsOpening: form.debtorsOpening !== '' ? Number(form.debtorsOpening) : 0,
      creditDays: form.creditDays !== '' ? Number(form.creditDays) : 0,
      creditLimit: form.creditLimit !== '' ? Number(form.creditLimit) : null,
      paymentTerms: form.paymentTerms.trim(),
      maritalStatus: form.maritalStatus,
      birthdate: form.birthdate || null,
    };

    if (isEdit && id) {
      updateContact.mutate(
        { id, payload },
        {
          onSuccess: () => {
            toast.success('Contact updated successfully.');
            navigate(`/contacts/${id}`);
          },
          onError: (err: any) => toast.error(contactSaveErrorMessage(err, 'Failed to update contact.')),
        }
      );
    } else {
      createContact.mutate(payload, {
        onSuccess: (res) => {
          toast.success('Contact created successfully.');
          if (onCreated && res?.id) {
            onCreated({
              id: res.id,
              name: payload.name,
              company: payload.company,
              phone: payload.phone,
              email: payload.email,
            });
            return;
          }
          navigate(res?.id ? `/contacts/${res.id}` : ROUTES.contacts);
        },
        onError: (err: any) => {
          const data = err?.response?.data;
          if (data?.code === 'CONTACT_OWNED_BY_OTHER' && data?.existingContact) {
            setDuplicateOwner({ ownerName: data.owner?.name || '', existingContact: data.existingContact });
            return;
          }
          toast.error(contactSaveErrorMessage(err, 'Failed to create contact.'));
        },
      });
    }
  };

  const isPending = createContact.isPending || updateContact.isPending;
  // No reassignment lock: contacts are shared and anyone may edit them.
  if (isEdit && contactLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (isEdit && (isError || !contact)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-12">
        <p className="text-body-base text-on-surface-variant font-medium">Could not load the contact details.</p>
        <Button variant="outline" onClick={() => navigate(ROUTES.contacts)}>
          Back to Contacts
        </Button>
      </div>
    );
  }

  return (
    <div className={embedded
      ? 'flex w-full flex-1 flex-col gap-6'
      : 'w-full flex-1 flex flex-col gap-6 p-4 md:p-6 lg:p-8 max-w-6xl mx-auto'}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      {/* The page chrome is dropped when this form is shown in a dialog. */}
      {!embedded && (
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-outline/60 pb-5">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => leave(isEdit && id ? `/contacts/${id}` : ROUTES.contacts)}
            aria-label="Back"
            className="rounded-xl h-10 w-10 border-outline bg-surface"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-body-xs font-semibold uppercase tracking-wider text-primary">CRM Directory</span>
              <span className="text-body-xs text-on-surface-variant">/</span>
              <span className="text-body-xs text-on-surface-variant">
                {isEdit ? (contact?.company || contact?.name || 'Edit Contact') : 'New Entry'}
              </span>
            </div>
            <h1 className="flex items-center gap-2.5 text-2xl font-bold text-on-surface mt-0.5">
              <UserPlus className="h-6 w-6 text-primary" />
              {isEdit ? 'Edit Contact Profile' : 'New Contact Profile'}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => leave(isEdit && id ? `/contacts/${id}` : ROUTES.contacts)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="contact-main-form"
            disabled={isPending}
            className="px-6 shadow-sm"
          >
            {isPending ? 'Saving Contact…' : isEdit ? 'Save Changes' : 'Create Contact'}
          </Button>
        </div>
      </header>

      )}

      {/* ── Form Body ────────────────────────────────────────────────────── */}
      <form id="contact-main-form" onSubmit={handleSubmit} className="flex flex-col gap-6 w-full">
        {duplicateInfo && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-body-sm text-amber-950 shadow-xs flex items-start gap-3 animate-in fade-in-50 duration-200">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-amber-950 text-body-sm">
                ⚠️ Already registered with {duplicateInfo.match.company || duplicateInfo.match.name}, Owner: {duplicateInfo.match.ownerName}
              </p>
              <p className="text-amber-800 text-caption mt-0.5">
                Matched on {duplicateInfo.on.toUpperCase()} ({duplicateInfo.on === 'phone' ? duplicateInfo.match.phone : duplicateInfo.match.gstin}).
              </p>
            </div>
          </div>
        )}

        {/* Top-Level Classification Card */}
        <div className="rounded-2xl border border-outline bg-surface p-5 shadow-sm flex flex-col gap-4">
          <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
            <Layers className="h-4 w-4 text-primary" />
            <h2 className="text-body-base font-semibold text-on-surface">Classification & Account Profile</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Contact Type"
              value={form.contactType}
              onChange={set('contactType')}
              options={CONTACT_TYPES}
            />

            <Select
              label="Customer Type"
              value={form.customerType}
              onChange={set('customerType')}
              options={CUSTOMER_TYPES}
            />

            <Select
              label="Category"
              value={form.category}
              onChange={set('category')}
              options={categoryOptions}
            />

            <Select
              label="Client Type (New / Old) *"
              value={form.clientType}
              onChange={set('clientType')}
              required
              options={[
                { value: '', label: 'Select Client Type (New / Old)...' },
                { value: 'New Client', label: 'New Client' },
                { value: 'Old Client', label: 'Old Client (Existing)' },
              ]}
            />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 border-b border-outline/80 overflow-x-auto pb-px">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-body-sm font-semibold rounded-t-xl transition-all border-b-2 whitespace-nowrap ${
                  active
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high/40'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* ── Tab 1: Basic Details ────────────────────────────────────────── */}
        {activeTab === 'basic' && (
          <div className="rounded-2xl border border-outline bg-surface p-6 shadow-sm flex flex-col gap-5 animate-in fade-in-50 duration-200">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <User className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Contact Information & Address</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <Input
                  label="Customer's Contact Person"
                  value={form.name}
                  onChange={set('name')}
                  placeholder="e.g. Rajesh Kumar (the client's person, not the BDM)"
                  autoFocus
                />
                <p className="text-data-xs text-muted-foreground">
                  The person at the client's side.{!form.name.trim() && ' If left blank, the company name will be used.'}
                </p>
              </div>

              <Input
                label={`Company / Business Name${!form.name.trim() ? ' *' : ''}`}
                value={form.company}
                onChange={set('company')}
                placeholder="e.g. Apex Industrial Works Pvt Ltd"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Phone Number"
                value={form.phone}
                onChange={set('phone')}
                inputMode="tel"
                placeholder="+91 98765 43210"
              />

              <Input
                label="WhatsApp Number"
                value={form.whatsapp}
                onChange={set('whatsapp')}
                inputMode="tel"
                placeholder="+91 98765 43210"
              />

              <Input
                label="Customer's Email"
                value={form.email}
                onChange={set('email')}
                type="email"
                placeholder="client@theircompany.com"
              />
            </div>

            <div className="rounded-xl border border-outline/70 bg-surface-container-low/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-body-sm font-semibold text-on-surface">Additional Mobile Numbers</p>
                  <p className="text-body-xs text-on-surface-variant">Store alternate numbers for the same client profile.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addExtraPhone}>
                  <Plus className="h-4 w-4 mr-1" /> Add mobile
                </Button>
              </div>
              {form.extraPhones.length > 0 && (
                <div className="mt-3 grid gap-2">
                  {form.extraPhones.map((phone, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={phone}
                        onChange={(e) => setExtraPhone(index, e.target.value)}
                        inputMode="tel"
                        placeholder="+91 98765 43210"
                      />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeExtraPhone(index)} aria-label="Remove mobile number">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4 border-t border-outline/60 pt-4">
              <div className="flex items-center gap-2 text-body-sm font-medium text-on-surface">
                <MapPin className="h-4 w-4 text-primary" />
                <span>Address Details</span>
              </div>

              <Input
                label="Street Address / Premises"
                value={form.address}
                onChange={set('address')}
                placeholder="Building No., Street, Industrial Area, Landmark..."
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <Select
                  label="Country"
                  value={form.country || 'India'}
                  onChange={set('country')}
                  options={countryOptions}
                />

                {isIndia ? (
                  <Select
                    label="State"
                    value={form.state}
                    onChange={set('state')}
                    options={stateOptions}
                  />
                ) : (
                  <Input
                    label="State / Province"
                    value={form.state}
                    onChange={set('state')}
                    placeholder="State, province or region"
                  />
                )}

                <Input
                  label="City"
                  value={form.city}
                  onChange={set('city')}
                  placeholder="e.g. Mumbai, Pune, Delhi"
                />

                <Input
                  label="Pincode"
                  value={form.pin}
                  onChange={set('pin')}
                  placeholder="e.g. 400001"
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 2: Additional Details ───────────────────────────────────── */}
        {activeTab === 'additional' && (
          <div className="rounded-2xl border border-outline bg-surface p-6 shadow-sm flex flex-col gap-5 animate-in fade-in-50 duration-200">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Statutory Identity & Details</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="GSTIN"
                value={form.gstin}
                onChange={set('gstin')}
                placeholder="27AAPFU0939F1ZZ"
              />

              <Input
                label="ECOM GSTIN"
                value={form.ecomGstin}
                onChange={set('ecomGstin')}
                placeholder="E-commerce GSTIN (if applicable)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="PAN Number"
                value={form.panNo}
                onChange={set('panNo')}
                placeholder="AAPFU0939F"
              />

              <Input
                label="Aadhar Number"
                value={form.aadharNo}
                onChange={set('aadharNo')}
                placeholder="XXXX-XXXX-XXXX"
              />

              <Select
                label="Gender"
                value={form.gender}
                onChange={set('gender')}
                options={GENDER_OPTIONS}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-outline/60 pt-4">
              <Input
                label="Designation / Position"
                value={form.position}
                onChange={set('position')}
                placeholder="e.g. Managing Director, Purchase Head"
              />

              <div className="flex flex-col gap-1.5">
                <span className="text-body-sm font-medium text-on-surface">Notes / Context</span>
                <textarea
                  value={form.notes}
                  onChange={set('notes')}
                  rows={3}
                  className="w-full rounded-xl border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface placeholder:text-on-surface-variant/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition resize-none"
                  placeholder="Key account details, preference notes, delivery instructions..."
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 3: Account Details ──────────────────────────────────────── */}
        {activeTab === 'account' && (
          <div className="rounded-2xl border border-outline bg-surface p-6 shadow-sm flex flex-col gap-5 animate-in fade-in-50 duration-200">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <CreditCard className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Financial Balances & Credit Terms</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Creditor Opening Balance (₹)"
                value={form.creditorsOpening}
                onChange={set('creditorsOpening')}
                inputMode="numeric"
                placeholder="Amount owed to vendor (0 if none)"
              />

              <Input
                label="Debtors Opening Balance (₹)"
                value={form.debtorsOpening}
                onChange={set('debtorsOpening')}
                inputMode="numeric"
                placeholder="Amount receivable from customer (0 if none)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-outline/60 pt-4">
              <Input
                label="Credit Days"
                value={form.creditDays}
                onChange={set('creditDays')}
                type="number"
                placeholder="e.g. 30, 45, 60"
              />

              <Input
                label="Credit Limit (₹)"
                value={form.creditLimit}
                onChange={set('creditLimit')}
                inputMode="numeric"
                placeholder="e.g. 500000"
              />

              <Input
                label="Payment Terms"
                value={form.paymentTerms}
                onChange={set('paymentTerms')}
                placeholder="e.g. Net 30, PDC on delivery, Advance"
              />
            </div>
          </div>
        )}

        {/* ── Tab 4: Anniversaries & Personal ─────────────────────────────── */}
        {activeTab === 'anniversaries' && (
          <div className="rounded-2xl border border-outline bg-surface p-6 shadow-sm flex flex-col gap-5 animate-in fade-in-50 duration-200">
            <div className="flex items-center gap-2 border-b border-outline/60 pb-3">
              <Calendar className="h-4 w-4 text-primary" />
              <h2 className="text-body-base font-semibold text-on-surface">Personal Milestones & Anniversaries</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Marital Status"
                value={form.maritalStatus}
                onChange={set('maritalStatus')}
                options={MARITAL_STATUS_OPTIONS}
              />

              <div>
                <label className="mb-1.5 block text-body-sm font-medium text-on-surface">
                  Birthdate
                </label>
                <input
                  type="date"
                  value={form.birthdate}
                  onChange={set('birthdate')}
                  className="w-full rounded-xl border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition"
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Bottom Actions Bar ──────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-outline bg-surface p-4 shadow-sm">
          <Button
            type="button"
            variant="ghost"
            onClick={() => leave(isEdit && id ? `/contacts/${id}` : ROUTES.contacts)}
          >
            Cancel
          </Button>

          <div className="flex items-center gap-2">
            <Button
              type="submit"
              disabled={isPending}
              className="px-6"
            >
              {isPending ? 'Saving Contact…' : isEdit ? 'Save Changes' : 'Create Contact'}
            </Button>
          </div>
        </div>
      </form>

      <Modal
        open={Boolean(duplicateOwner)}
        onClose={() => setDuplicateOwner(null)}
        title="Client already exists"
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setDuplicateOwner(null)}>
              Cancel
            </Button>
            {!embedded && duplicateOwner && (
              <Button type="button" onClick={() => navigate(`/contacts/${duplicateOwner.existingContact.id}`)}>
                Open contact
              </Button>
            )}
            {embedded && onCreated && duplicateOwner && (
              <Button
                type="button"
                onClick={() => {
                  onCreated(duplicateOwner.existingContact);
                  setDuplicateOwner(null);
                }}
              >
                Link to existing contact
              </Button>
            )}
          </div>
        }
      >
        {duplicateOwner && (
          <p className="text-body-sm text-on-surface-variant">
            This phone number already belongs to{' '}
            <span className="font-bold text-on-surface">{duplicateOwner.existingContact.company || duplicateOwner.existingContact.name}</span>
            {' '}in Contacts{duplicateOwner.ownerName ? ` (added by ${duplicateOwner.ownerName})` : ''}.
            {embedded && onCreated
              ? ' Link this record to the existing contact instead of creating a duplicate.'
              : ' Open the existing contact and work on it there — contacts are shared by everyone.'}
          </p>
        )}
      </Modal>
    </div>
  );
}

export default ContactFormPage;
